import React, { useState, useContext } from 'react';
import useApi from '../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../utils/secure-route/AuthContext';
import { useToastHandler } from '../../common/ToastHandler';
import ReusableModal from '../AppPAges/ManageSettings/ReusableModal';
import ReusableTable from '../../common/ReusableComponent/reusableTable/ReusableTable';
import useSwal from '../../common/ReusableComponent/useSwal';
import { ToastContainer } from 'react-toastify';
import useMyPermissions from '../../../utils/permissions/useMyPermissions';
import Can from '../../../utils/permissions/Can';

function CameraGroup() {
  const { user } = useContext(AuthContext);
  const { can } = useMyPermissions();
  const { Post, Put, Delete, GetPaginatedData } = useApi();
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const triggerRefresh = () => setRefreshTrigger(p => p + 1);

  const fetchData = async ({ page = 1, searchTerm = '' } = {}) => {
    try {
      setLoading(true);
      const response = await GetPaginatedData('cameraGroupData', { page, search: searchTerm });
      const data = response?.data || [];
      const totalPages = Math.ceil((response?.totalCount || data.length) / 10);
      return { data, totalPages };
    } catch (error) {
      console.error('Error fetching camera groups:', error);
      return { data: [], totalPages: 1 };
    } finally {
      setLoading(false);
    }
  };

  const handleShow = (idOrItem = null, maybeItem = null) => {
    const item = maybeItem || (typeof idOrItem === 'object' ? idOrItem : null);
    if (item) {
      setEditing(item);
      setName(item.name);
      setDescription(item.description);
    } else {
      setEditing(null);
      setName('');
      setDescription('');
    }
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { store: user.store, name, description };
    try {
      if (editing) {
        await Put('cameraGroupData', editing.id, payload);
        handleToast({ type: 'SUCCESS', message: 'Camera group updated successfully!' });
      } else {
        await Post('cameraGroupData', payload);
        handleToast({ type: 'SUCCESS', message: 'Camera group created successfully!' });
      }
      handleClose();
      triggerRefresh();
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Error saving camera group.' });
    }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try {
      await Delete('cameraGroupData', id);
      handleToast({ type: 'SUCCESS', message: 'Camera group deleted successfully!' });
      triggerRefresh();
    } catch (error) {
      handleToast({ type: 'ERROR', message: 'Error deleting camera group.' });
    }
  };

  const columns = [
    { header: 'Name', field: 'name' },
    { header: 'Description', field: 'description' },
  ];

  return (
    <>
      <div className="responsive-box" style={{ padding: '20px' }}>
        <div className="header-row">
          <div className="setting-title">Camera Groups</div>
          <Can module="mointerService" action="add">
            <button
              onClick={() => handleShow()}
              style={{
                backgroundColor: '#FFAF32',
                border: 'none',
                borderRadius: '10px',
                padding: '8px 16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                height: '42px',
              }}
            >
              + Add Group
            </button>
          </Can>
        </div>

        <ReusableModal
          show={showModal}
          handleClose={handleClose}
          title={editing ? 'Edit Camera Group' : 'Add Camera Group'}
          width="500px"
        >
          <form onSubmit={handleSubmit}>
            <div className="d-flex flex-column mt-4" style={{ gap: '16px' }}>
              <div className="form-group product-field">
                <input
                  type="text"
                  className="form-control-sales"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  required
                />
                <label className="floating-label">Group Name</label>
              </div>
              <div className="form-group product-field">
                <input
                  type="text"
                  className="form-control-sales"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                />
                <label className="floating-label">Description</label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  style={{
                    backgroundColor: '#282e26',
                    color: '#F4FFEE',
                    borderRadius: '40px',
                    width: '160px',
                    height: '38px',
                    fontWeight: 'bold',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {editing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </ReusableModal>

        <ReusableTable
          fetchData={fetchData}
          columns={columns}
          showAction={true}
          enablePagination={true}
          showFooter={true}
          onEdit={can("mointerService", "change") ? handleShow : undefined}
          onDelete={can("mointerService", "delete") ? handleDelete : undefined}
          loading={loading}
          refreshKey={refreshTrigger}
        />
      </div>
      <ToastContainer />
    </>
  );
}

export default CameraGroup;
